import { SporsmalType } from '../types/generated-types';
import { Kundeinfo } from '../types/internal-types';
import {
    BesvartSporsmal,
    copyNextSvarToCurrentSporsmal,
    CurrentSporsmal,
    toBesvartSporsmal,
    toCurrentSporsmal,
} from './reducer/current-steg';
import { currentSporsmalReducer } from './reducer/currentSporsmalReducer';
import { UpdateSporsmalSideAction } from './reducer/sporsmalsflyt-actions';
import {
    findSkadeDato,
    findSporsmalThatIsTriggeredByUtsattForgreining,
    getIdentifikatorType,
    getNextSteg,
} from './reducer/sporsmalsflyt-state';
import { Sporsmal, Sporsmalsflyt, SporsmalsGruppe } from './reducer/sporsmalsflyt-types';

export interface SporsmalVisibilityState {
    sporsmalId: string;
    show: boolean;
}

export interface SporsmalsflytCollection {
    currentSporsmalList: CurrentSporsmal[];
    sporsmalVisibilityState: SporsmalVisibilityState[];
}

export const getVisibleSporsmalFromCollection = (sporsmalsflytCollection: SporsmalsflytCollection): CurrentSporsmal[] =>
    sporsmalsflytCollection.sporsmalVisibilityState
        .filter((sporsmalVisibilityState) => sporsmalVisibilityState.show)
        .map((sporsmalVisibilityState) =>
            sporsmalsflytCollection.currentSporsmalList.find(
                (current) => current.sporsmal.id === sporsmalVisibilityState.sporsmalId,
            ),
        );

const findRestOfVisible = (
    sporsmalsList: Sporsmal[],
    sporsmalsGruppeList: SporsmalsGruppe[],
    besvarteSporsmal: BesvartSporsmal[],
    currentSporsmalList: CurrentSporsmal[],
    currentlyVisible: string[],
    current: CurrentSporsmal,
): string[] => {
    const besvarteSporsmalSideSpormal: BesvartSporsmal[] = currentSporsmalList
        .map(toBesvartSporsmal)
        .filter((besvartSporsmal) => besvartSporsmal && currentlyVisible.includes(besvartSporsmal.sporsmal.id));

    const nextSporsmal: Sporsmal | undefined = getNextSteg(
        sporsmalsList,
        sporsmalsGruppeList,
        [...besvarteSporsmal, ...besvarteSporsmalSideSpormal],
        current,
    );
    if (nextSporsmal) {
        return findRestOfVisible(
            sporsmalsList,
            sporsmalsGruppeList,
            besvarteSporsmal,
            currentSporsmalList,
            [...currentlyVisible, nextSporsmal.id],
            currentSporsmalList.find((b) => b.sporsmal.id === nextSporsmal.id),
        );
    }
    return currentlyVisible;
};

const findFirstVisibleSporsmal = (
    currentSporsmallist: CurrentSporsmal[],
    sporsmalsflyt: Sporsmalsflyt,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentSporsmal => {
    const firstSporsmal = sporsmalsflyt.sporsmalsList[0];
    if (firstSporsmal.type !== SporsmalType.UtsattForgreining) {
        return currentSporsmallist.find((currentSporsmal) => currentSporsmal.sporsmal.id === firstSporsmal.id);
    }

    const firstNonUtsattSporsmal = findSporsmalThatIsTriggeredByUtsattForgreining(
        firstSporsmal,
        besvarteSporsmal,
        sporsmalsflyt.sporsmalsList,
        sporsmalsflyt.triggered,
    );

    if (firstNonUtsattSporsmal) {
        return currentSporsmallist.find((currentSporsmal) => currentSporsmal.sporsmal.id === firstNonUtsattSporsmal.id);
    }
    throw Error('SporsmalSide/SporsmalList inneholder ingen spørsmål');
};

export const findVisibleSporsmalIds = (
    list: CurrentSporsmal[],
    sporsmalsflyt: Sporsmalsflyt,
    besvarteSporsmal: BesvartSporsmal[],
): string[] => {
    const first: CurrentSporsmal = findFirstVisibleSporsmal(list, sporsmalsflyt, besvarteSporsmal);

    return findRestOfVisible(
        sporsmalsflyt.sporsmalsList,
        sporsmalsflyt.triggered,
        besvarteSporsmal,
        list,
        [first.sporsmal.id],
        first,
    );
};

interface NewVisibilityListHelperState {
    visibleSporsmalIds: string[];
    oldVisibilityState: SporsmalVisibilityState[];
    newVisibilityState: SporsmalVisibilityState[];
}

export const createNewVisibilityList = (state: NewVisibilityListHelperState): SporsmalVisibilityState[] => {
    const allVisibleSporsmalAreAlreadyAddedToVisibilityState = state.visibleSporsmalIds.length === 0;
    if (allVisibleSporsmalAreAlreadyAddedToVisibilityState) {
        return [
            ...state.newVisibilityState,
            ...state.oldVisibilityState.map((sporsmalState) => ({
                ...sporsmalState,
                show: false,
            })),
        ];
    }

    const allSporsmalFromOldVisibilityStateAreAlreadyAddedToNewState = state.oldVisibilityState.length === 0;
    if (allSporsmalFromOldVisibilityStateAreAlreadyAddedToNewState) {
        return [
            ...state.newVisibilityState,
            ...state.visibleSporsmalIds.map((sporsmalId) => ({
                sporsmalId,
                show: true,
            })),
        ];
    }

    if (state.visibleSporsmalIds[0] === state.oldVisibilityState[0].sporsmalId) {
        return createNewVisibilityList({
            visibleSporsmalIds: state.visibleSporsmalIds.slice(1),
            oldVisibilityState: state.oldVisibilityState.slice(1),
            newVisibilityState: [...state.newVisibilityState, { sporsmalId: state.visibleSporsmalIds[0], show: true }],
        });
    }

    const theNextVisibleSporsmalAlreadyExistsInTheOldVisibilityState = state.oldVisibilityState.some(
        (sporsmalState) => sporsmalState.sporsmalId === state.visibleSporsmalIds[0],
    );
    if (theNextVisibleSporsmalAlreadyExistsInTheOldVisibilityState) {
        return createNewVisibilityList({
            visibleSporsmalIds: state.visibleSporsmalIds,
            oldVisibilityState: state.oldVisibilityState.slice(1),
            newVisibilityState: [
                ...state.newVisibilityState,
                {
                    sporsmalId: state.oldVisibilityState[0].sporsmalId,
                    show: false,
                },
            ],
        });
    }

    return createNewVisibilityList({
        visibleSporsmalIds: state.visibleSporsmalIds.slice(1),
        oldVisibilityState: state.oldVisibilityState,
        newVisibilityState: [
            ...state.newVisibilityState,
            {
                sporsmalId: state.visibleSporsmalIds[0],
                show: true,
            },
        ],
    });
};

export const getSporsmalVisibilityStatesFromCollection = (
    sporsmalsflytCollection: SporsmalsflytCollection,
): { sporsmal: CurrentSporsmal; show: boolean }[] =>
    sporsmalsflytCollection.sporsmalVisibilityState.map((sporsmalState) => ({
        sporsmal: sporsmalsflytCollection.currentSporsmalList.find(
            (currentSporsmal) => currentSporsmal.sporsmal.id === sporsmalState.sporsmalId,
        ),
        show: sporsmalState.show,
    }));

const getAllSporsmalFromSporsmalsflyt = (sporsmalsflyt: Sporsmalsflyt): Sporsmal[] => [
    ...sporsmalsflyt.sporsmalsList,
    ...sporsmalsflyt.triggered.flatMap((sporsmalsGruppe) => sporsmalsGruppe.sporsmalsList),
];

export const updateVisibilityList = (
    updatedCurrentSporsmalList: CurrentSporsmal[],
    sporsmalsflyt: Sporsmalsflyt,
    oldVisibilityState: SporsmalVisibilityState[],
    besvarteSporsmal: BesvartSporsmal[],
): SporsmalVisibilityState[] => {
    const visibleSporsmalIds: string[] = findVisibleSporsmalIds(
        updatedCurrentSporsmalList,
        sporsmalsflyt,
        besvarteSporsmal,
    );
    return createNewVisibilityList({ visibleSporsmalIds, oldVisibilityState, newVisibilityState: [] });
};

export const initializeSporsmalsflytCollection = (
    sporsmalsflyt: Sporsmalsflyt,
    besvarteSporsmal: BesvartSporsmal[],
    kundeinfo?: Kundeinfo,
): SporsmalsflytCollection => {
    const currentSporsmalList = getAllSporsmalFromSporsmalsflyt(sporsmalsflyt).map((sporsmal) =>
        toCurrentSporsmal(
            sporsmal,
            besvarteSporsmal,
            kundeinfo,
            getIdentifikatorType(besvarteSporsmal, kundeinfo?.offentligId),
            findSkadeDato(besvarteSporsmal),
        ),
    );
    return {
        currentSporsmalList,
        sporsmalVisibilityState: updateVisibilityList(currentSporsmalList, sporsmalsflyt, [], besvarteSporsmal),
    };
};

export const updateSporsmalsflytCollection = (
    sporsmalsflytCollection: SporsmalsflytCollection,
    sporsmalsflyt: Sporsmalsflyt,
    action: UpdateSporsmalSideAction,
    besvarteSporsmalFromOutsideCollection: BesvartSporsmal[],
): SporsmalsflytCollection => {
    const updatedCurrentSporsmalList = sporsmalsflytCollection.currentSporsmalList
        .map((currentSporsmal) => {
            if (currentSporsmal.sporsmal.id === action.sporsmalId) {
                return currentSporsmalReducer(currentSporsmal, besvarteSporsmalFromOutsideCollection, action.action);
            }
            return currentSporsmal;
        })
        .filter(
            (currentSporsmal, index, currentSporsmalList) =>
                index === currentSporsmalList.findIndex((t) => t.sporsmal.id === currentSporsmal.sporsmal.id),
        );
    return {
        ...sporsmalsflytCollection,
        currentSporsmalList: updatedCurrentSporsmalList,
        sporsmalVisibilityState: updateVisibilityList(
            updatedCurrentSporsmalList,
            sporsmalsflyt,
            sporsmalsflytCollection.sporsmalVisibilityState,
            besvarteSporsmalFromOutsideCollection,
        ),
    };
};

export const copyNextSvarToSporsmalsflytCollection = (
    sporsmalsflytCollection: SporsmalsflytCollection,
    nextSporsmalsflytCollection: SporsmalsflytCollection,
    sporsmalsflyt: Sporsmalsflyt,
    besvarteSporsmal: BesvartSporsmal[],
): SporsmalsflytCollection => {
    const currentSporsmalList = sporsmalsflytCollection.currentSporsmalList.map((emptySporsmal) => {
        const filledOutSporsmal = nextSporsmalsflytCollection.currentSporsmalList.find(
            (n) => n.sporsmal.id === emptySporsmal.sporsmal.id,
        );
        if (filledOutSporsmal) {
            return copyNextSvarToCurrentSporsmal(emptySporsmal, filledOutSporsmal, besvarteSporsmal);
        }
        return emptySporsmal;
    });

    return {
        ...sporsmalsflytCollection,
        currentSporsmalList,
        sporsmalVisibilityState: updateVisibilityList(
            currentSporsmalList,
            sporsmalsflyt,
            sporsmalsflytCollection.sporsmalVisibilityState,
            besvarteSporsmal,
        ),
    };
};
